import type { App } from '../app/App';
import type { SaveData } from '../game/systems/model';
import { sanitizeSave } from '../game/systems/save';
import { Emitter } from '../game/core/Emitter';
import { modal } from '../ui/components';
import { h } from '../ui/dom';
import { t } from '../i18n';
import { ApiError } from './ApiClient';

const META_KEY = 'paper-legends/cloud';
/**
 * Changes are uploaded this long after the last one (or at once when the
 * player leaves the page). The browser copy is always saved; a missed upload
 * just happens on the next start, so waiting longer costs nothing but writes.
 */
const PUSH_DELAY_MS = 30_000;

/** What this browser last agreed on with the cloud. */
interface Meta {
  playerId: string;
  rev: number;
  /** The notebook's savedAt when it last matched the cloud copy. */
  syncedAt: number;
}

export type CloudStatus = 'off' | 'syncing' | 'synced' | 'offline' | 'unavailable';

/**
 * Keeps the notebook in the cloud while signed in (guest or Google). The
 * browser copy stays the working copy; the cloud copy follows a few seconds
 * behind. When both changed (two devices), the player picks which to keep.
 * The device's own sign-in token never leaves the browser.
 */
export class CloudSync extends Emitter<{ status: CloudStatus }> {
  status: CloudStatus = 'off';
  private meta: Meta | null = null;
  private timer = 0;
  private running: Promise<void> | null = null;
  private again = false;
  private applying = false;
  private playerId: string | null = null;

  constructor(private readonly app: App) {
    super();
    try {
      const raw = JSON.parse(localStorage.getItem(META_KEY) ?? 'null') as Meta | null;
      if (raw && typeof raw.playerId === 'string' && typeof raw.rev === 'number' && typeof raw.syncedAt === 'number') this.meta = raw;
    } catch {
      /* no meta: treated as a new device */
    }
    app.store.saved.add(() => {
      if (!this.applying && this.playerId) this.schedule();
    });
    // Leaving the page (tab switch, app to background): upload what is waiting now.
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'hidden' && this.timer && this.playerId) {
        window.clearTimeout(this.timer);
        this.timer = 0;
        void this.run(() => this.push());
      }
    });
  }

  private setStatus(s: CloudStatus): void {
    if (this.status === s) return;
    this.status = s;
    this.emit('status', s);
  }

  private saveMeta(meta: Meta | null): void {
    this.meta = meta;
    try {
      if (meta) localStorage.setItem(META_KEY, JSON.stringify(meta));
      else localStorage.removeItem(META_KEY);
    } catch {
      /* storage full: the next start just compares again */
    }
  }

  /** Start syncing for the signed-in player (safe to call again). */
  start(): Promise<void> {
    const online = this.app.store.state.online;
    if (!online) return Promise.resolve();
    this.playerId = online.playerId;
    return this.run(() => this.pull());
  }

  /** Stop syncing and forget the link (sign out). */
  stop(): void {
    window.clearTimeout(this.timer);
    this.playerId = null;
    this.saveMeta(null);
    this.setStatus('off');
  }

  /** Upload now (e.g. before switching devices). */
  syncNow(): Promise<void> {
    window.clearTimeout(this.timer);
    return this.run(() => this.pull());
  }

  private schedule(): void {
    if (this.status === 'unavailable') return;
    window.clearTimeout(this.timer);
    this.timer = window.setTimeout(() => {
      this.timer = 0;
      void this.run(() => this.push());
    }, PUSH_DELAY_MS);
  }

  /** One sync at a time; a request during a sync runs once more after it. */
  private run(fn: () => Promise<void>): Promise<void> {
    if (this.running) {
      this.again = true;
      return this.running;
    }
    this.running = (async () => {
      try {
        do {
          this.again = false;
          await fn();
        } while (this.again);
      } catch (err) {
        if (err instanceof ApiError && err.status === 404) this.setStatus('unavailable');
        else this.setStatus('offline');
      } finally {
        this.running = null;
      }
    })();
    return this.running;
  }

  private localCopy(): Omit<SaveData, 'online'> {
    const { online: _online, ...rest } = this.app.store.state;
    return rest;
  }

  private changedHere(): boolean {
    const s = this.app.store.state;
    if (!this.meta || this.meta.playerId !== this.playerId) return hasProgress(s);
    return s.savedAt > this.meta.syncedAt;
  }

  private async pull(): Promise<void> {
    const pid = this.playerId;
    if (!pid) return;
    this.setStatus('syncing');
    this.app.api.token = this.app.store.state.online?.token ?? null;
    const cloud = await this.app.api.getSave();
    if (cloud.rev === 0 || cloud.save === null) return this.upload(0);
    const sameAsLast = this.meta?.playerId === pid && this.meta.rev === cloud.rev;
    if (sameAsLast) {
      if (this.changedHere()) return this.upload(cloud.rev);
      this.setStatus('synced');
      return;
    }
    if (this.changedHere()) {
      const keep = await askWhichNotebook(this.app.store.state, sanitizeSave(cloud.save), cloud.updatedAt);
      if (keep === 'device') return this.upload(cloud.rev);
    }
    this.apply(cloud.save, cloud.rev);
  }

  private async push(): Promise<void> {
    if (!this.playerId) return;
    if (!this.changedHere() && this.meta?.playerId === this.playerId) return;
    if (!this.meta || this.meta.playerId !== this.playerId) return this.pull();
    return this.upload(this.meta.rev);
  }

  private async upload(baseRev: number): Promise<void> {
    const pid = this.playerId!;
    this.setStatus('syncing');
    const savedAt = this.app.store.state.savedAt;
    try {
      const res = await this.app.api.putSave(this.localCopy(), baseRev);
      this.saveMeta({ playerId: pid, rev: res.rev, syncedAt: savedAt });
      this.setStatus('synced');
    } catch (err) {
      // Another device wrote first: compare again.
      if (err instanceof ApiError && err.status === 409) return this.pull();
      throw err;
    }
  }

  private apply(save: unknown, rev: number): void {
    const next = sanitizeSave(save);
    next.online = this.app.store.state.online;
    this.applying = true;
    try {
      this.app.store.replace(next);
    } finally {
      this.applying = false;
    }
    this.saveMeta({ playerId: this.playerId!, rev, syncedAt: this.app.store.state.savedAt });
    this.setStatus('synced');
  }
}

/** Anything worth keeping in a notebook. */
function hasProgress(s: SaveData): boolean {
  return s.heroes.length > 0 || s.monsters.length > 0 || s.memories.length > 0;
}

function summary(s: SaveData): string {
  const hero = s.heroes.find((x) => x.id === s.activeHeroId) ?? s.heroes[0];
  return [
    hero ? t('{name}, level {level}', { name: hero.name, level: hero.level }) : t('No hero yet'),
    t('{n} monsters', { n: s.monsters.length }),
    t('{n} coins', { n: s.coins }),
  ].join(' · ');
}

/** Both copies changed: the player chooses. */
async function askWhichNotebook(here: SaveData, cloud: SaveData, cloudAt: number): Promise<'device' | 'cloud'> {
  const body = h(
    'div',
    { class: 'notebook-choice' },
    h('p', null, t('This device and your account have different notebooks. Which one do you want to keep?')),
    h('div', { class: 'choice-card' }, h('strong', null, t('This device')), h('span', null, summary(here))),
    h(
      'div',
      { class: 'choice-card' },
      h('strong', null, t('Your account (saved {when})', { when: cloudAt ? new Date(cloudAt).toLocaleString() : '?' })),
      h('span', null, summary(cloud)),
    ),
    h('p', { class: 'hand-hint' }, t('The other notebook will be replaced.')),
  );
  const v = await modal(
    t('Two notebooks found'),
    body,
    [
      { label: t('Keep this device'), value: 'device', color: 'blue' },
      { label: t('Use my account'), value: 'cloud', variant: 'primary', color: 'yellow' },
    ],
    { dismissable: false },
  );
  return v === 'device' ? 'device' : 'cloud';
}
